import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage } from "@/App";
import { Button } from "@/components/ui/button";
import { Briefcase, Check, Sparkles } from "lucide-react";
import BackBar from "@/components/BackBar";

/**
 * Public membership page.
 *
 * After the membership/pricing update:
 *  - All public services (Jobs, Education, Housing Guide, Tourism, etc.) are FREE
 *    and require no subscription.
 *  - Customer/Driver/Jobs/Education plans were removed.
 *  - Service Provider membership is **not** advertised here — providers manage
 *    activation/renewal from inside their own "My Services" dashboard.
 *
 * We keep this route alive (rather than deleting it) because older flows
 * (`/payment-success`, profile "Upgrade" button, etc.) still link here.
 */
export default function Subscriptions() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <DashboardLayout title={t("العضوية", "Membership")}>
      <BackBar
        to="/dashboard"
        label_ar="الرئيسية"
        label_en="Home"
        crumbs={[
          { to: "/dashboard", ar: "الرئيسية", en: "Home" },
          { ar: "العضوية", en: "Membership" },
        ]}
      />

      <div className="space-y-6" data-testid="subscriptions-page">
        {/* ── Free access banner ──────────────────────────────────────────── */}
        <div className="glass-card rounded-2xl p-6 lg:p-8 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-500/10 blur-[120px] rounded-full" />
          <div className="relative">
            <Sparkles className="w-10 h-10 text-emerald-400 mb-3" />
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">
              {t("جميع الخدمات الأساسية مجانية", "All core services are free")}
            </h2>
            <p className="text-slate-300 max-w-2xl">
              {t(
                "لا توجد رسوم اشتراك للوصول إلى دليل الوظائف، التعليم، السكن، السياحة، أو الاستفادة من المساعد الذكي. استمتع باستخدام جميع المزايا دون قيود.",
                "There are no subscription fees to access the Jobs guide, Education, Housing Guide, Tourism, or the AI assistant. Enjoy every feature without limits."
              )}
            </p>
            <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6 mt-5 text-sm text-slate-200">
              {[
                t("دليل فرص العمل", "Jobs guide"),
                t("دليل التعليم والجامعات", "Education & Universities"),
                t("دليل السكن", "Housing guide"),
                t("اكتشف أمريكا", "Discover America"),
                t("المساعد الذكي", "AI Assistant"),
                t("الدردشة العامة", "Community Chat"),
              ].map((label) => (
                <li key={label} className="flex items-center gap-2">
                  <Check size={16} className="text-emerald-400" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Service-provider CTA ───────────────────────────────────────── */}
        <div className="glass-card rounded-2xl p-6 lg:p-8">
          <Briefcase className="w-10 h-10 text-yellow-400 mb-3" />
          <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">
            {t("هل أنت مقدّم خدمة؟", "Are you a service provider?")}
          </h3>
          <p className="text-slate-300 max-w-2xl mb-4">
            {t(
              "إذا كنت طبيباً، محامياً، صاحب مطعم، شركة شحن، كهربائياً، سبّاكاً، مترجماً، محاسباً، أو صاحب أي نشاط محلي — يمكنك إدارة عضويتك (تفعيل / تجديد / حالة الاشتراك) من لوحة 'صفحتي كمقدم خدمة'.",
              "If you're a doctor, lawyer, restaurant owner, shipping company, electrician, plumber, translator, accountant, or any local business — manage your membership (activate / renew / status) from the 'My Services' dashboard."
            )}
          </p>
          <div className="text-sm text-slate-400 mb-5">
            <span className="text-emerald-300">{t("🎁 الشهر الأول مجاناً", "🎁 First month free")}</span>
            <span className="mx-2 text-slate-600">·</span>
            <span className="text-yellow-300">{t("🔥 الشهر الثاني $4.99", "🔥 Second month $4.99")}</span>
            <span className="mx-2 text-slate-600">·</span>
            <span className="text-slate-200">{t("💼 من الشهر الثالث $15/شهر", "💼 From the third month $15/mo")}</span>
          </div>
          <Button
            onClick={() => navigate("/my-provider")}
            className="btn-primary"
            data-testid="goto-my-provider-btn"
          >
            <Briefcase size={16} className="ml-2" />
            {t("اذهب إلى صفحتي كمقدم خدمة", "Go to My Services")}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
